import {
  BANK_NAME,
  CLEARING_NUMBER_MAX_LENGTH,
  ACCOUNT_NUMBER_MAX_LENGTH,
  BBAN_MAX_LENGTH,
  BRITE_BRAND_NAME,
  BRITE_DEFAULT_COUNTRY_ID,
} from './bank.constants';
import { BankMethodValue, ExistingBankRecord, BriteCustomerContext } from './bank.types';
import { CreateBriteSessionRequest } from './brite.service';
import { BriteSurface } from './brite-resume';

export function isForeignOrRevolut(bankName?: string | null): boolean {
  return bankName === BANK_NAME.FOREIGN || bankName === BANK_NAME.REVOLUT;
}

export function isRevolut(bankName?: string | null): boolean {
  return bankName === BANK_NAME.REVOLUT;
}

export function isForeign(bankName?: string | null): boolean {
  return bankName === BANK_NAME.FOREIGN;
}

// Unified rule across all surfaces: clearing number is 4-5 digits, account
// number is 6+ digits, digits only. (Previously surfaces #1/#2 only rejected
// special characters without enforcing length — this tightens that.)
export function isValidClearingNumber(value?: string | null): boolean {
  return !!value && /^\d{4,5}$/.test(value);
}

export function isValidAccountNumber(value?: string | null): boolean {
  return !!value && /^\d{6,}$/.test(value.slice(0, ACCOUNT_NUMBER_MAX_LENGTH));
}

// react-hook-form-style `validate` callbacks, for surfaces still using RHF.
export const validateClearingNumberRHF = (value: string, errorMessage: string) =>
  isValidClearingNumber(value) || errorMessage;

export const validateAccountNumberRHF = (value: string, errorMessage: string) =>
  isValidAccountNumber(value) || errorMessage;

export function sanitizeDigits(value: string, maxLength: number): string {
  return value.replace(/\D/g, '').slice(0, maxLength);
}

export function sanitizeClearingNumber(value: string): string {
  return sanitizeDigits(value, CLEARING_NUMBER_MAX_LENGTH);
}

export function sanitizeAccountNumber(value: string): string {
  return sanitizeDigits(value, ACCOUNT_NUMBER_MAX_LENGTH);
}

export function sanitizeBban(value: string): string {
  return sanitizeDigits(value, BBAN_MAX_LENGTH);
}

export interface BankDisplayFields {
  isForeignBankSelected: boolean;
  isRevolutSelected: boolean;
  isOtherBankSelected: boolean;
  isSwedishBankSelected: boolean;
}

// Replaces the duplicated view/edit branching logic across all 4 surfaces.
export function getBankDisplayFields(bankName?: string | null): BankDisplayFields {
  const foreign = isForeign(bankName);
  const revolut = isRevolut(bankName);
  const hasBank = !!bankName?.trim();
  return {
    isForeignBankSelected: foreign,
    isRevolutSelected: revolut,
    isOtherBankSelected: false, // bank field holds the actual typed name, never the "Other Bank" marker — see buildManualBankPayload
    isSwedishBankSelected: hasBank && !foreign && !revolut,
  };
}

// Builds the exact payload shape the existing save endpoint
// (PUT /settings/save-customer-salary) expects, for the manual paths only.
// The Brite path never calls this — see useBrite's onCompleted flow.
export function buildManualBankPayload(value: BankMethodValue): {
  bank: string;
  clearingNumber?: string;
  accountNumber?: string;
  ibanNumber?: string;
  bicNumber?: string;
  bbanNumber?: string;
} {
  if (value.method === 'foreign' || value.method === 'revolut') {
    return {
      bank: value.method === 'revolut' ? BANK_NAME.REVOLUT : BANK_NAME.FOREIGN,
      ibanNumber: value.ibanNumber,
      bicNumber: value.bicNumber,
      clearingNumber: "",
      accountNumber: ""
    };
  }
  if (value.method === 'brite') {
    return {
      bank: value.bank,
      bbanNumber: value.bbanNumber || value.ibanNumber,
      clearingNumber: "",
      accountNumber: "",
    };
  }
  // 'other-swedish' — the saved bank name is the user's typed bank name, not
  // the literal "Other Bank" marker (matches existing behavior in all 4 surfaces).
  return {
    bank: value.otherBankName || BANK_NAME.OTHER,
    clearingNumber: value.clearingNumber,
    accountNumber: value.accountNumber,
  };
}

// Single source of truth for "does this customer have enough bank info on
// file to be paid" — used to gate invoice creation/sending across surfaces
// (invoice-preview-modal, invoice-footer, InvoiceTool). Same precedence as
// deriveBankMethodValue/isBriteLinked: an explicit IBAN+BIC or account+clearing
// record always counts; a Brite marker only counts when isBriteSessionAccount
// isn't explicitly false (the backend doesn't populate it on every account yet,
// so a missing flag is treated as session-backed — see profile-settings.slice.ts).
export function hasCompleteBankInfo(record?: ExistingBankRecord | null): boolean {
  if (!record) return false;
  const hasIbanBic = !!record.ibanNumber && !!record.bicNumber;
  const hasAccountClearing = !!record.accountNumber && !!record.clearingNumber;
  const isBriteLinked = !!record.isBriteIdExist && record.isBriteSessionAccount !== false && !hasIbanBic;
  return hasIbanBic || hasAccountClearing || isBriteLinked;
}

// Maps an existing saved bank record back into a BankMethodValue so
// pre-existing accounts (including pre-Brite Swedish bank names) open into
// the right panel. Any non-blank, non-Foreign, non-Revolut bank name is
// treated as the National / "other bank" bucket, since the predefined
// Swedish bank list no longer exists as a selectable option — Brite replaces
// it, and there's currently no backend marker distinguishing a Brite-linked
// account from a manually entered one (flagged in the plan as unconfirmed;
// revisit if/when the backend adds one).
export function deriveBankMethodValue(record?: ExistingBankRecord | null): BankMethodValue {
  const bank = record?.bank || '';
  const hasIbanBic = !!record?.ibanNumber && !!record?.bicNumber;
  if (isRevolut(bank)) {
    return {
      group: 'international',
      method: 'revolut',
      bank,
      ibanNumber: record?.ibanNumber || '',
      bicNumber: record?.bicNumber || '',
    };
  }
  if (isForeign(bank) || hasIbanBic) {
    return {
      group: 'international',
      method: 'foreign',
      bank: bank || BANK_NAME.FOREIGN,
      ibanNumber: record?.ibanNumber || '',
      bicNumber: record?.bicNumber || '',
    };
  }
  if (record?.isBriteIdExist && record?.isBriteSessionAccount !== false) {
    return {
      group: 'national',
      method: 'brite',
      bank,
      bbanNumber: record?.bbanNumber || '',
    };
  }
  // No bank on file yet (brand-new user) — default to the primary Brite
  // path rather than the manual fallback. Surfaces where Brite isn't
  // offered yet (allowBrite={false}) still show manual fields regardless,
  // since BankMethodSelector's own render condition checks allowBrite.
  if (!bank) {
    return { group: 'national', method: 'brite', bank: '' };
  }
  return {
    group: 'national',
    method: 'other-swedish',
    bank,
    otherBankName: bank,
    clearingNumber: record?.clearingNumber || '',
    accountNumber: record?.accountNumber || '',
  };
}

// Builds the Brite create-session request from a surface-supplied customer
// context. Centralizes the still-unconfirmed values (brand name) behind
// env-driven defaults so they only need updating in one place once the
// backend contract is confirmed.
export function buildBriteSessionPayload(
  context: BriteCustomerContext,
  locale: string,
  surface: BriteSurface
): CreateBriteSessionRequest {
  const countryId = context.customerAddress?.country_id || BRITE_DEFAULT_COUNTRY_ID;
  // const { redirectUri, deeplinkRedirect } = buildBriteCallbackUrls(surface);
  return {
    countryId,
    brandName: '',
    locale,
    // redirectUri,
    // deeplinkRedirect,
    // customerId: context.customerId ? String(context.customerId) : undefined,
    customerFirstname: context.firstName,
    customerLastname: context.lastName,
    customerDob: context.dob || '',
    customerEmail: context.email,
    customerAddress: {
      city: context.customerAddress?.city || '',
      street: context.customerAddress?.street || '',
      postalCode: context.customerAddress?.postal_code || '',
      countryId,
    },
  };
}
